import React, { createContext, useContext, useEffect, useState } from 'react';
import { DEFAULT_CALENDAR_VIEW, IEvent, useCalendarSettings } from '..';

interface Filters {
	selectedEventTypes: string[];
	selectedEventTags: string[];
	selectedEventLocations: string[];
	selectedCountries: string[];
	selectedStates: string[];
	selectedCities: string[];
}

interface FiltersContextProps {
	events: IEvent[];
	filteredEvents: IEvent[];
	defaultFilters: {
		eventTypes: string[];
		eventTags: string[];
		eventLocations: string[];
		countries: string[];
		states: string[];
		cities: string[];
	};
	filters: Filters;
	searchTerm: string;
	currentView: string;
	currentMonth: string;
	setEvents: React.Dispatch<React.SetStateAction<IEvent[]>>;
	setFilters: React.Dispatch<React.SetStateAction<Filters>>;
	setFilteredEvents: React.Dispatch<React.SetStateAction<IEvent[]>>;
	setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
	setCurrentView: React.Dispatch<React.SetStateAction<string>>;
	setCurrentMonth: React.Dispatch<React.SetStateAction<string>>;
}

const FiltersContext = createContext<FiltersContextProps | undefined>(undefined);

export const FiltersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [defaultFilters, setDefaultFilters] = useState({
		eventTypes: [],
		eventTags: [],
		eventLocations: [],
		countries: [],
		states: [],
		cities: [],
	});
	const [searchTerm, setSearchTerm] = useState('');
	const [filters, setFilters] = useState<Filters>({
		selectedEventTypes: [],
		selectedEventTags: [],
		selectedEventLocations: [],
		selectedCountries: [],
		selectedStates: [],
		selectedCities: [],
	});
	const [events, setEvents] = useState<IEvent[] | null>(null);
	const [filteredEvents, setFilteredEvents] = useState<IEvent[] | null>(null);

	const { settings } = useCalendarSettings();
	const [currentView, setCurrentView] = useState<string>(settings?.defaultView || DEFAULT_CALENDAR_VIEW);
	const [currentMonth, setCurrentMonth] = useState();

	useEffect(() => {
		setCurrentView(settings?.defaultView || DEFAULT_CALENDAR_VIEW);
	}, [settings]);

	useEffect(() => {
		if (window.calendarPlusData) {
			const events = window.calendarPlusData.map((item) => ({
				...item,
				eventType: item?.eventType && typeof item.eventType === 'string' ? item.eventType.split(',') : [],
				tags: item?.tags && typeof item.tags === 'string' ? item.tags.split(',') : [],
				start: new Date(item.start),
				end: new Date(item.end),
			}));

			setEvents(events);
			setFilteredEvents(events);
		}
	}, []);
	useEffect(() => {
		if (events?.length > 0) {
			const eventTypes = [...new Set(events.flatMap((event) => event.eventType))].filter((event) => event);
			const eventTags = [...new Set(events.flatMap((event) => event.tags))].filter((event) => event);
			const eventLocations = [...new Set(events.flatMap((event) => event.venue))].filter((event) => event);

			const countries = Array.from(new Set(events.map((event) => event.country).filter(Boolean)));
			const states = Array.from(new Set(events.map((event) => event.state).filter(Boolean)));
			const cities = Array.from(new Set(events.map((event) => event.city).filter(Boolean)));

			setDefaultFilters({ eventTypes, eventTags, eventLocations, countries, states, cities });
			setFilters({
				selectedEventTypes: [],
				selectedEventTags: [],
				selectedEventLocations: [],
				selectedCountries: [],
				selectedStates: [],
				selectedCities: [],
			});
		}
	}, [events]);

	return (
		<FiltersContext.Provider
			value={{
				events: events || [],
				filteredEvents: filteredEvents || [],
				defaultFilters,
				filters,
				searchTerm,
				currentView,
				currentMonth,
				setEvents,
				setFilters,
				setFilteredEvents,
				setSearchTerm,
				setCurrentView,
				setCurrentMonth,
			}}
		>
			{children}
		</FiltersContext.Provider>
	);
};

export const useFilters = (): FiltersContextProps => {
	const context = useContext(FiltersContext);
	if (!context) {
		throw new Error('useFilters must be used within a FiltersProvider');
	}
	return context;
};
